import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Stack,
  Chip,
} from '@mui/material';
import {
  Build as BuildIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  PlayCircleOutline as PlayCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  TaskAlt as TaskAltIcon,
  Payments as PaymentsIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import Navigation from '../components/Navigation';
import TutorialModal from '../components/Tutorial/TutorialModal';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialRole, setTutorialRole] = useState(null);

  React.useEffect(() => {
    if (user) {
      if (user.role === 'contractor') navigate('/contractor');
      else if (user.role === 'homeowner') navigate('/homeowner');
      else if (user.role === 'admin') navigate('/admin');
    }
  }, [user, navigate]);

  const openTutorial = (role = null) => {
    setTutorialRole(role);
    setTutorialOpen(true);
  };

  const trustChips = [
    { label: 'Detailed Estimates', icon: <DescriptionIcon /> },
    { label: 'Sign-off Approvals', icon: <TaskAltIcon /> },
    { label: 'Tracked Payments', icon: <PaymentsIcon /> },
    { label: 'Job-site Photos', icon: <PhotoCameraIcon /> },
  ];

  const roles = [
    {
      title: 'Admin',
      icon: <AdminIcon sx={{ fontSize: 60 }} />,
      description: 'Oversee all projects and provide final approvals',
      color: '#0d47a1',
      path: '/login?role=admin',
      roleKey: 'admin',
    },
    {
      title: 'Contractor',
      icon: <BuildIcon sx={{ fontSize: 60 }} />,
      description: 'Create and manage construction projects',
      color: '#1976d2',
      path: '/login?role=contractor',
      roleKey: 'contractor',
    },
    {
      title: 'Homeowner',
      icon: <HomeIcon sx={{ fontSize: 60 }} />,
      description: 'Review and approve project work',
      color: '#42a5f5',
      path: '/login?role=homeowner',
      roleKey: 'homeowner',
    },
  ];

  return (
    <>
      {/* Top Navigation Bar */}
      <Navigation
        showNotifications={false}
        showLogout={false}
      />

      {/* Main Content - Architectural Blueprint Theme */}
      <Box
        sx={{
          minHeight: '100vh',
          background: '#003d5c',
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(45deg, transparent 48%, rgba(255, 255, 255, 0.03) 49%, rgba(255, 255, 255, 0.03) 51%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(255, 255, 255, 0.03) 49%, rgba(255, 255, 255, 0.03) 51%, transparent 52%)
          `,
          backgroundSize: '100px 100px, 100px 100px, 10px 10px, 10px 10px, 50px 50px, 50px 50px',
          backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0, 0 0',
          position: 'relative',
          py: 4,
          // Blueprint border frame
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '20px',
            left: '20px',
            right: '20px',
            bottom: '20px',
            border: '2px solid rgba(255, 255, 255, 0.1)',
            pointerEvents: 'none',
          },
          // Top-left corner registration mark
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '25px',
            left: '25px',
            width: '50px',
            height: '50px',
            borderTop: '3px solid rgba(255, 255, 255, 0.2)',
            borderLeft: '3px solid rgba(255, 255, 255, 0.2)',
            pointerEvents: 'none',
          },
          // Top-right corner registration mark
          '& > div::before': {
            content: '""',
            position: 'absolute',
            top: '25px',
            right: '25px',
            width: '50px',
            height: '50px',
            borderTop: '3px solid rgba(255, 255, 255, 0.2)',
            borderRight: '3px solid rgba(255, 255, 255, 0.2)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          // Bottom-left corner registration mark
          '& > div::after': {
            content: '""',
            position: 'absolute',
            bottom: '25px',
            left: '25px',
            width: '50px',
            height: '50px',
            borderBottom: '3px solid rgba(255, 255, 255, 0.2)',
            borderLeft: '3px solid rgba(255, 255, 255, 0.2)',
            pointerEvents: 'none',
            zIndex: 0,
          }
        }}
      >
      {/* Bottom-right corner mark - using absolutely positioned div */}
      <Box
        sx={{
          content: '""',
          position: 'absolute',
          bottom: '25px',
          right: '25px',
          width: '50px',
          height: '50px',
          borderBottom: '3px solid rgba(255, 255, 255, 0.2)',
          borderRight: '3px solid rgba(255, 255, 255, 0.2)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Container maxWidth="lg">
        <Paper
          elevation={10}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.97)',
          }}
        >
          {/* Hero */}
          <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', letterSpacing: 2, fontWeight: 600 }}
            >
              Congrow
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '2.25rem', md: '3.25rem' },
                lineHeight: 1.15,
                mt: 1,
              }}
            >
              Construction projects, finally on the same page.
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 720, mx: 'auto', mt: 2, fontWeight: 400 }}
            >
              Transparent estimates, item-by-item approvals, and tracked payments —
              built for contractors, homeowners, and the admins who keep them in sync.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 4 }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/register')}
                sx={{ px: 4, py: 1.25, fontWeight: 600 }}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<PlayCircleIcon />}
                onClick={() => openTutorial()}
                sx={{ px: 4, py: 1.25, fontWeight: 600 }}
              >
                How it works
              </Button>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent="center"
              sx={{ mt: 4, gap: 1 }}
            >
              {trustChips.map((c) => (
                <Chip
                  key={c.label}
                  label={c.label}
                  icon={c.icon}
                  variant="outlined"
                  sx={{ borderColor: 'rgba(13, 71, 161, 0.25)', color: 'primary.dark' }}
                />
              ))}
            </Stack>
          </Box>

          {/* Role cards */}
          <Typography
            variant="body2"
            align="center"
            sx={{ color: 'text.secondary', mb: 2, fontWeight: 600, letterSpacing: 1 }}
          >
            CHOOSE YOUR ROLE
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {roles.map((role) => (
              <Grid item xs={12} sm={6} md={4} key={role.title}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderTop: `4px solid ${role.color}`,
                    height: '100%',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                    },
                  }}
                  onClick={() => navigate(role.path)}
                >
                  <Box sx={{ color: role.color, mb: 1 }}>
                    {role.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight={600}>
                    {role.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {role.description}
                  </Typography>
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: role.color,
                        '&:hover': { backgroundColor: role.color, opacity: 0.9 },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(role.path);
                      }}
                    >
                      Login as {role.title}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openTutorial(role.roleKey);
                      }}
                      sx={{ color: role.color, textTransform: 'none' }}
                    >
                      See how it works for {role.title.toLowerCase()}s
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box textAlign="center" mt={{ xs: 4, md: 6 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Don't have an account?
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/register')}
              sx={{ mt: 1 }}
            >
              Register Now
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>

    <TutorialModal
      open={tutorialOpen}
      onClose={() => setTutorialOpen(false)}
      initialRole={tutorialRole}
    />
    </>
  );
};

export default LandingPage;
